import React from "react";

export interface RecognitionState {
  consent: "anonymous" | "named" | "unspecified";
  displayName?: string;
}

interface Props {
  billingFullName: string;
  value: RecognitionState;
  onChange: (next: RecognitionState) => void;
  disabled?: boolean;
}

/**
 * Three-option radio for donor recognition. Default is anonymous; the
 * billing name is offered as a one-click option, and a custom name field
 * appears only when the donor selects "List me as: ___".
 *
 * Ideologically: the donor controls visibility — admin cannot override.
 */
export function RecognitionPicker({ billingFullName, value, onChange, disabled }: Props) {
  const isCustom =
    value.consent === "named" &&
    value.displayName !== "" &&
    value.displayName !== billingFullName;

  return (
    <fieldset className="border border-hairline p-5 bg-paper-2">
      <legend className="font-mono text-[10px] uppercase tracking-widest text-claude font-bold px-2">
        Recognition
      </legend>
      <p className="font-serif text-sm text-ink-2 mb-4 leading-relaxed">
        How should the Foundation list your support on the public ledger?
      </p>

      <div className="space-y-3 text-sm font-mono text-ink-2">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="recognition"
            disabled={disabled}
            checked={value.consent === "named" && value.displayName === billingFullName}
            onChange={() => onChange({ consent: "named", displayName: billingFullName })}
            className="mt-1"
          />
          <span>
            List me as <strong className="text-ink">{billingFullName || "(your billing name)"}</strong>
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="recognition"
            disabled={disabled}
            checked={isCustom}
            onChange={() =>
              onChange({
                consent: "named",
                displayName: value.displayName && value.displayName !== billingFullName
                  ? value.displayName
                  : "",
              })
            }
            className="mt-1"
          />
          <span className="flex-1">
            List me as:{" "}
            <input
              type="text"
              disabled={disabled || !isCustom}
              placeholder="custom name"
              value={isCustom ? value.displayName ?? "" : ""}
              onChange={(e) => onChange({ consent: "named", displayName: e.target.value })}
              className="border-b border-hairline bg-paper px-1 py-0.5 font-mono text-sm text-ink focus:outline-none focus:border-ink ml-1 w-48"
            />
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="recognition"
            disabled={disabled}
            checked={value.consent === "anonymous" || value.consent === "unspecified"}
            onChange={() => onChange({ consent: "anonymous" })}
            className="mt-1"
          />
          <span>Anonymous</span>
        </label>
      </div>

      <p className="font-mono text-[10px] text-ink-3 leading-relaxed mt-4 italic">
        You can change this any time via the link in your receipt email. The Foundation never overrides this choice.
      </p>
    </fieldset>
  );
}
